import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage, ChatbotState } from '../types';
import { useTranslation } from 'react-i18next';
import { createGeminiMessage } from '../api/gemini';
import { useChatHistory } from '../hooks/useChatHistory';

interface ChatbotContextType {
  chatState: ChatbotState;
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (message: string) => Promise<void>;
  clearMessages: () => void;
  getMessagesLeft: () => number;
  setChatState: React.Dispatch<React.SetStateAction<ChatbotState>>;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

interface ChatbotProviderProps {
  children: React.ReactNode;
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { messages: storedMessages, addMessage, canSendMessage, getMessagesLeft } = useChatHistory();
  
  const [chatState, setChatState] = useState<ChatbotState>({
    messages: storedMessages.length ? storedMessages : [{
      id: uuidv4(),
      content: t('chatbot.welcomeMessage'),
      isUser: false,
      timestamp: new Date()
    }],
    isOpen: false,
    isLoading: false,
    error: null,
    isExpanded: false
  });
  
  const openChat = useCallback(() => {
    setChatState(prev => ({ ...prev, isOpen: true }));
  }, []);
  
  const closeChat = useCallback(() => {
    setChatState(prev => ({ ...prev, isOpen: false }));
  }, []);
  
  const toggleChat = useCallback(() => {
    setChatState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);
  
  const sendMessage = useCallback(async (content: string) => {
    if (!canSendMessage()) {
      setChatState(prev => ({
        ...prev,
        error: t('chatbot.maxMessagesError')
      }));
      return;
    }

    const userMessage: ChatMessage = {
      id: uuidv4(),
      content,
      isUser: true,
      timestamp: new Date()
    };
    
    if (!addMessage(userMessage)) {
      setChatState(prev => ({
        ...prev,
        error: t('chatbot.maxMessagesError')
      }));
      return;
    }
    
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null
    }));
    
    try {
      // Envoi les messages précédents pour le contexte de la conversation
      const response = await createGeminiMessage(
        content,
        i18n.language,
        chatState.messages
      );
      
      const botMessage: ChatMessage = {
        id: uuidv4(),
        content: response,
        isUser: false,
        timestamp: new Date()
      };
      
      addMessage(botMessage);
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, botMessage],
        isLoading: false
      }));
    } catch (error) {
      console.error('Error in sendMessage:', error);
      
      // Utiliser une réponse de secours en cas d'échec de l'API
      const fallbackResponse = getFallbackResponse(content, i18n.language);
      const botMessage: ChatMessage = {
        id: uuidv4(),
        content: fallbackResponse,
        isUser: false,
        timestamp: new Date()
      };
      
      addMessage(botMessage);
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, botMessage],
        isLoading: false,
        error: t('chatbot.apiErrorMessage')
      }));
    }
  }, [t, i18n.language, chatState.messages, addMessage, canSendMessage]);
  
  const clearMessages = useCallback(() => {
    setChatState(prev => ({
      ...prev,
      messages: [{
        id: uuidv4(),
        content: t('chatbot.welcomeMessage'),
        isUser: false,
        timestamp: new Date()
      }],
      error: null
    }));
  }, [t]);
  
  // Fonction de secours en cas d'échec de l'API
  const getFallbackResponse = (message: string, language: string): string => {
    if (language === 'fr') {
      return message.toLowerCase().includes('prix') 
        ? "Nous proposons un service de question spécifique à 10€, et tous les autres services sont disponibles sur devis. Consultez notre page de tarification pour plus d'informations."
        : "Merci pour votre question. Comment puis-je vous aider davantage?";
    } else {
      return message.toLowerCase().includes('price') 
        ? "We offer a specific question service for €10, and all other services are available upon request. You can check our pricing page for more information."
        : "Thank you for your question. How else can I assist you?";
    }
  };
  
  return (
    <ChatbotContext.Provider value={{
      chatState,
      isOpen: chatState.isOpen,
      openChat,
      closeChat,
      toggleChat,
      sendMessage,
      clearMessages,
      getMessagesLeft,
      setChatState
    }}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = (): ChatbotContextType => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};